import { applications, taxonomies, xApiStatements } from 'z-js-commons';
import { X_API_ZANICHELLI } from '../config';
import { RESUME_LOGIN_SUCCESS } from '../actions/auth';
import { ADD_TUTORIAL_START } from '../actions/tutorials';
import { EDIT_EXERCISE_FROM_DRAFT } from '../actions/exercise';
import * as types from '../constants/ActionTypes';
import { getItemTypeNames, getSubjectsName, getYearName } from './InvalsiUtils';
import { exerciseTaxonomyNames, getHomeworkDraftTaxonomy } from '../lib/TaxonomyUtils';
import { getSubjectNameFromExercises } from './ExerciseUtils';

function getIsbnFromStateAndBookId(list, bookId) {
    let isbn;
    if (list) {
        const bookDetails = list.find(bookList => bookList.ids.find(id => id === bookId));
        if (bookDetails && bookDetails.isbn) {
            isbn = bookDetails.isbn;
        }
    }
    return isbn;
}

function getActor() {
    return {
        objectType: 'Agent',
        account: {
            homePage: '',
            name: ''
        }
    };
}

function getContext() {
    return {
        platform: applications.CREA_VERIFICHE
    };
}

export default function getStatementByActionType(action, state) {
    const actor = getActor();
    const context = getContext();

    switch (action.type) {
        // Login events
        case RESUME_LOGIN_SUCCESS: {
            const { payload } = action;
            if (!state.auth.isIdpLogin && !payload.traceLogin) {
                return {
                    actor: getActor(),
                    verb: {
                        id: `${X_API_ZANICHELLI}/verbs/accessed`
                    },
                    object: {
                        objectType: 'Activity',
                        id: `${window.location.href}`
                    },
                    context: {
                        platform: applications.CREA_VERIFICHE,
                        extensions: {
                            [`${X_API_ZANICHELLI}/login/mode`]: null
                        }
                    }
                };
            } else if (payload.traceLogin) {
                return xApiStatements.resumeLoginSuccess(X_API_ZANICHELLI, action, applications.CREA_VERIFICHE);
            }
        }

        // Search events (exercises and homeworks)
        case types.SEARCH_EXERCISES: {
            let isbns;
            if (state && state.Exercise && state.Exercise.filters && state.Exercise.filters.books) {
                isbns = state.Exercise.filters.books.map(book => book.isbn);
                isbns = Array.isArray(isbns) && isbns.length > 0 ? isbns : undefined;
            }

            return xApiStatements.searchExercisesOrHomework(X_API_ZANICHELLI, action, state, applications.CREA_VERIFICHE, false, isbns);
        }

        case types.SEARCH_EDITORIAL_HOMEWORK: {
            const payloadAction = {
                type: action.type,
                ...action.payload
            };

            let isbns;

            if (state && state.EditorialHomework && state.EditorialHomework.filters && state.EditorialHomework.filters.books) {
                isbns = state.EditorialHomework.filters.books.map(book => book.isbn);
                isbns = Array.isArray(isbns) && isbns.length > 0 ? isbns : undefined;
            }

            return xApiStatements.searchExercisesOrHomework(X_API_ZANICHELLI, payloadAction, state, applications.CREA_VERIFICHE, true, isbns);
        }

        case types.GET_INVALSI_EXERCISE_LIST: {
            const {
                traceData,
                taxonomyIdList,
                itemTypesId,
                searchTerm,
                isSuggestionSelected,
                subjectsId,
                fromYearOrdinal,
                toYearOrdinal,
                favouriteOnly
            } = action;
            if (!traceData || !traceData.subjectsList || !traceData.yearsList || !traceData.itemTypesList) {
                return null;
            }

            const subjectsFilter = getSubjectsName(traceData.subjectsList, subjectsId);
            const fromYearFilter = getYearName(traceData.yearsList, fromYearOrdinal);
            const toYearFilter = getYearName(traceData.yearsList, toYearOrdinal);
            const itemTypesFilter = getItemTypeNames(traceData.itemTypesList, itemTypesId);
            let taxonomyPathString = null;
            if (traceData.invalsiContentTaxonomy && traceData.invalsiContentTaxonomy.length > 0 && taxonomyIdList && taxonomyIdList.length > 0) {
                taxonomyPathString = taxonomies.findContentTaxonomyPathString(traceData.invalsiContentTaxonomy, taxonomyIdList[0].split('.'));
            }
            return {
                actor: actor,
                verb: {
                    id: `${X_API_ZANICHELLI}/verbs/searched`
                },
                object: {
                    objectType: 'Activity',
                    id: `${X_API_ZANICHELLI}/activities/exercises`,
                    definition: {
                        extensions: {
                            [`${X_API_ZANICHELLI}/keys/searchStyle`]: isSuggestionSelected ? 'suggestion' : 'full text',
                            ...(taxonomyPathString && { [`${X_API_ZANICHELLI}/keys/exercises/taxonomy`]: taxonomyPathString }),
                            [`${X_API_ZANICHELLI}/filters/searchTerm`]: searchTerm,
                            ...(subjectsFilter && subjectsFilter.length > 0 && { [`${X_API_ZANICHELLI}/filters/subjects`]: subjectsFilter }),
                            ...(fromYearFilter !== '' && { [`${X_API_ZANICHELLI}/filters/fromYear`]: fromYearFilter }),
                            ...(toYearFilter !== '' && { [`${X_API_ZANICHELLI}/filters/toYear`]: toYearFilter }),
                            ...(itemTypesFilter && itemTypesFilter.length > 0 && { [`${X_API_ZANICHELLI}/filters/types`]: itemTypesFilter }),
                            [`${X_API_ZANICHELLI}/filters/favouriteOnly`]: favouriteOnly,
                            [`${X_API_ZANICHELLI}/keys/exercises/origin`]: 'INVALSI'
                        }
                    }
                },
                context: context
            };
        }

        case types.GET_INVALSI_HOMEWORK_LIST: {
            const { subjectsId, fromYearOrdinal, toYearOrdinal, traceData } = action;
            if (!traceData || !traceData.subjectsInvalsi || !traceData.yearsList) {
                return null;
            }
            const subjectsFilter = getSubjectsName(traceData.subjectsInvalsi, subjectsId);
            const fromYearFilter = getYearName(traceData.yearsList, fromYearOrdinal);
            const toYearFilter = getYearName(traceData.yearsList, toYearOrdinal);
            return {
                actor: actor,
                verb: {
                    id: `${X_API_ZANICHELLI}/verbs/searched`
                },
                object: {
                    objectType: 'Activity',
                    id: `${X_API_ZANICHELLI}/activities/homework`,
                    definition: {
                        extensions: {
                            ...(subjectsFilter && subjectsFilter.length > 0 && { [`${X_API_ZANICHELLI}/filters/subjects`]: subjectsFilter }),
                            ...(fromYearFilter !== '' && { [`${X_API_ZANICHELLI}/filters/fromYear`]: fromYearFilter }),
                            ...(toYearFilter !== '' && { [`${X_API_ZANICHELLI}/filters/toYear`]: toYearFilter }),
                            [`${X_API_ZANICHELLI}/keys/homework/origin`]: 'INVALSI'
                        }
                    }
                },
                context: context
            };
        }

        // Exercises events
        case types.OPEN_EXERCISE_PREVIEW: {
            let exerciseBookId;
            let isbn;

            if (action && action.exercise && action.exercise.book && action.exercise.book.id) {
                exerciseBookId = action.exercise.book.id;
            }
            if (state && state.SearchFormReducer && state.SearchFormReducer.booksList) {
                isbn = getIsbnFromStateAndBookId(state.SearchFormReducer.booksList, exerciseBookId);
            }

            return xApiStatements.openExercisePreview(X_API_ZANICHELLI, action, state, applications.CREA_VERIFICHE, isbn);
        }

        case types.ADD_EXERCISE_TO_HOMEWORK:
        case types.ADD_EXERCISE_TO_CART: {
            const { exercise, exerciseAddedToCart } = action;
            const tmpExercise = exercise ? exercise : exerciseAddedToCart;
            return xApiStatements.addExercise(X_API_ZANICHELLI, state, applications.CREA_VERIFICHE, tmpExercise);
        }

        case types.ADD_EXERCISES_TO_HOMEWORK:
        case types.ADD_EXERCISES_TO_CART: {
            const { newAddedExercises } = action;
            if (!newAddedExercises || newAddedExercises.length === 0) {
                return null;
            }
            return newAddedExercises.map(exercise => {
                return {
                    actor: actor,
                    verb: {
                        id: `${X_API_ZANICHELLI}/verbs/added`
                    },
                    object: {
                        objectType: 'Activity',
                        id: `${X_API_ZANICHELLI}/activities/exercises/${exercise.id}`,
                        definition: {
                            extensions: {
                                ...(exercise.originName && { [`${X_API_ZANICHELLI}/keys/exercises/origin`]: exercise.originName }),
                                ...(exercise.book && exercise.book.id && { [`${X_API_ZANICHELLI}/keys/exercises/books`]: exercise.book }),
                                ...(exercise.type.label && { [`${X_API_ZANICHELLI}/keys/exercises/types`]: exercise.type.label }),
                                [`${X_API_ZANICHELLI}/keys/exercises/owner`]: exercise.UGC ? 'teacher' : 'editor',
                                ...(exercise.subject && exercise.subject.name && { [`${X_API_ZANICHELLI}/keys/exercises/matter`]: exercise.subject.name })
                            }
                        }
                    },
                    context: context
                };
            });
        }

        case types.UPDATE_EXERCISE_SUCCESS:
        case types.SAVE_EXERCISE_SUCCESS: {
            const { exercise, originalExercise } = action;

            return {
                actor: actor,
                verb: {
                    id: `${X_API_ZANICHELLI}/verbs/${action.type === types.UPDATE_EXERCISE_SUCCESS ? 'updated' : originalExercise.id ? 'edited' : 'saved'}`
                },
                object: {
                    objectType: 'Activity',
                    id: `${X_API_ZANICHELLI}/activities/exercises/${exercise.id}`,
                    definition: {
                        extensions: {
                            [`${X_API_ZANICHELLI}/keys/exercises/types`]: exercise.type.label,
                            ...(originalExercise && originalExercise.id && { [`${X_API_ZANICHELLI}/keys/exercises/originalId`]: originalExercise.id }),
                            ...(exercise.taxonomy && { [`${X_API_ZANICHELLI}/keys/exercises/taxonomy`]: exerciseTaxonomyNames(exercise.taxonomy) }),
                            ...(originalExercise &&
                                originalExercise.originName && { [`${X_API_ZANICHELLI}/keys/exercises/origin`]: originalExercise.originName }),
                            ...(originalExercise && { [`${X_API_ZANICHELLI}/keys/exercises/owner`]: originalExercise.UGC ? 'teacher' : 'editor' }),
                            ...(exercise.subject && exercise.subject.name && { [`${X_API_ZANICHELLI}/keys/exercises/matter`]: exercise.subject.name })
                        }
                    }
                },
                context: context
            };
        }

        case EDIT_EXERCISE_FROM_DRAFT: {
            return {
                actor: actor,
                verb: {
                    id: `${X_API_ZANICHELLI}/verbs/started_creation`
                },
                object: {
                    objectType: 'Activity',
                    id: `${X_API_ZANICHELLI}/activities/exercises`
                },
                context: context
            };
        }

        // Homeworks events
        case types.SAVE_HOMEWORK_SUCCESS:
        case types.UPDATE_HOMEWORK: {
            let subjects = [];
            let sourceHomeworkId;
            let isbn;
            if (state.Homework) {
                subjects = getSubjectNameFromExercises(state.Homework.exercises);
            }

            const trackVerb = action.type === types.UPDATE_HOMEWORK ? 'updated' : action.homework.isEditorialHomework ? 'copied' : 'saved';

            if (trackVerb === 'copied' && action && action.homework && action.homework.sourceHomeworkId) {
                sourceHomeworkId = action.homework.sourceHomeworkId;

                if (state && state.EditorialHomework && state.EditorialHomework.homeworkList) {
                    let bookId = state.EditorialHomework.homeworkList.find(homework => homework._id === sourceHomeworkId).bookId;

                    if (state && state.SearchFormReducer && state.SearchFormReducer.booksList) {
                        isbn = getIsbnFromStateAndBookId(state.SearchFormReducer.booksList, bookId);
                    }
                }
            }

            if (action.homework) action.homework._id = action.homeworkId;
            else return null;
            if (!action.homework.exercises) action.homework.exercises = [];
            if (action.homework.exercises.length > 0) action.homework.taxonomy = getHomeworkDraftTaxonomy(action.homework.exercises);
            return xApiStatements.saveOrUpdateHomework(X_API_ZANICHELLI, state, applications.CREA_VERIFICHE, action.homework, trackVerb, subjects, isbn);
        }

        case types.CALL_T2P_SUCCESS: {
            let homeworkID;
            let isbn;

            if (action && action.homeworkId) {
                homeworkID = action.homeworkId;
            }

            if (state && state.EditorialHomework && state.EditorialHomework.homeworkList) {
                let homework = state.EditorialHomework.homeworkList.find(homework => homework._id === homeworkID);

                let bookId;
                if (homework && homework.bookId) {
                    bookId = homework.bookId;

                    if (state && state.SearchFormReducer && state.SearchFormReducer.homeworkBooksList) {
                        isbn = getIsbnFromStateAndBookId(state.SearchFormReducer.homeworkBooksList, bookId);
                    }
                }
            }

            const { audio, fileType, highReadability, homeworkId, homeworkType, rows, exercises, taxonomy, originName, subjects } = action;
            return {
                actor: actor,
                verb: {
                    id: `${X_API_ZANICHELLI}/verbs/generated`
                },
                object: {
                    objectType: 'Activity',
                    id: `${X_API_ZANICHELLI}/activities/homework/${homeworkId}`,
                    definition: {
                        extensions: {
                            ...(originName && { [`${X_API_ZANICHELLI}/keys/export/homework/origin`]: originName }),
                            ...(isbn && { [`${X_API_ZANICHELLI}/keys/book/isbn`]: isbn }),
                            [`${X_API_ZANICHELLI}/keys/export/homework/highReadability`]: highReadability,
                            [`${X_API_ZANICHELLI}/keys/export/homework/attachedAudio`]: audio,
                            [`${X_API_ZANICHELLI}/keys/export/homework/owner`]: homeworkType,
                            [`${X_API_ZANICHELLI}/keys/export/homework/rows`]: rows,
                            [`${X_API_ZANICHELLI}/keys/export/homework/exercises/list`]: exercises,
                            [`${X_API_ZANICHELLI}/keys/export/homework/exercises/count`]: exercises.length,
                            [`${X_API_ZANICHELLI}/keys/export/homework/taxonomy`]: taxonomy,
                            [`${X_API_ZANICHELLI}/keys/export/homework/fileType`]: fileType,
                            [`${X_API_ZANICHELLI}/keys/homework/matter/list`]: subjects
                        },
                        type: `${X_API_ZANICHELLI}/activities/homework`
                    }
                },
                context: context
            };
        }

        case ADD_TUTORIAL_START: {
            const { objectId } = action;
            return xApiStatements.closeTutorial(X_API_ZANICHELLI, state, applications.CREA_VERIFICHE, objectId);
        }

        // Default
        default:
            return null;
    }
}
